import React from "react";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { KNOWLEDGE_BASE } from "@/constants/testIds";
import {
	preflightUpload,
	preflightErrorCopy,
	parseUploadError,
	uploadErrorCopyFor,
	extensionOf,
	titleFromFilename,
	formatBytes,
} from "@/lib/knowledgeBase";

/**
 * Phase 4F — Slice 3: Document upload dialog.
 *
 * Contract-critical behavior:
 *   - Exactly one POST /workspaces/{ws}/documents per confirmed submission.
 *     A synchronous inFlightRef guard fires before the request so rapid
 *     clicks / repeated Enter presses cannot produce duplicates.
 *   - No AbortController: the backend has no idempotency key and cancelling
 *     mid-upload leaves the server state ambiguous. The Dialog is instead
 *     LOCKED while uploading (see onOpenChange guard).
 *   - Success data is read only from `response.data` (not from
 *     `response.detail.data` and not from `response.data.detail`).
 *   - Errors are parsed only from `err.response.data.detail.error /.message`.
 *   - content_sha256, storage paths, and signed URLs are never displayed.
 *   - No Retry, no Delete, no polling, no timers, no direct Supabase.
 */

const SAFE_TYPE_FALLBACK = "Unknown type";

function typeLabel(file) {
	if (!file) return "";
	if (typeof file.type === "string" && file.type.length > 0) return file.type;
	const ext = extensionOf(file.name || "");
	if (ext.length > 0) return `.${ext} (browser did not report a MIME type)`;
	return SAFE_TYPE_FALLBACK;
}

export default function KBUploadDialog({ workspaceId, isOpen, onClose, onUploaded }) {
	const [file, setFile] = React.useState(null);
	const [title, setTitle] = React.useState("");
	const [titleTouched, setTitleTouched] = React.useState(false);
	const [message, setMessage] = React.useState(null); // { tone: "error"|"info", text }
	const [uploading, setUploading] = React.useState(false);

	const mountedRef = React.useRef(true);
	const inFlightRef = React.useRef(false);

	React.useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	// Reset local state whenever the Dialog transitions from closed to open.
	// Do NOT reset while a request is in flight.
	React.useEffect(() => {
		if (isOpen && !inFlightRef.current) {
			setFile(null);
			setTitle("");
			setTitleTouched(false);
			setMessage(null);
			setUploading(false);
		}
	}, [isOpen]);

	const onFileChange = (event) => {
		const next = event.target.files && event.target.files[0] ? event.target.files[0] : null;
		setFile(next);
		setMessage(null);
		// Suggest a title from the basename whenever the user has NOT
		// deliberately edited the title. This covers the "second file replaces
		// the first file's suggestion" case, and never overwrites an
		// intentional edit (including a deliberate clear).
		if (next && !titleTouched) {
			setTitle(titleFromFilename(next.name));
		}
	};

	const onTitleChange = (event) => {
		setTitle(event.target.value);
		setTitleTouched(true);
		setMessage(null);
	};

	const closeIfIdle = () => {
		if (inFlightRef.current) return;
		if (typeof onClose === "function") onClose();
	};

	const doUpload = async () => {
		// Synchronous double-submit guard: must run BEFORE any async work.
		if (inFlightRef.current) return;

		const preflightKey = preflightUpload({ file, title });
		if (preflightKey !== null) {
			setMessage({ tone: "error", text: preflightErrorCopy(preflightKey) });
			return;
		}
		if (!workspaceId) {
			setMessage({ tone: "error", text: uploadErrorCopyFor({ kind: "unexpected" }) });
			return;
		}

		inFlightRef.current = true;
		setUploading(true);
		setMessage({ tone: "info", text: "Uploading…" });

		const form = new FormData();
		form.append("title", title.trim());
		form.append("file", file, file.name);

		try {
			const response = await api.post(
				`/workspaces/${workspaceId}/documents`,
				form
			);
			if (!mountedRef.current) return;
			// Only trust confirmed frontend-safe fields — never render or retain
			// content_sha256 / storage_path / signed URL / bucket.
			const data = response && response.data ? response.data : null;
			const documentId = data && typeof data.document_id === "string" ? data.document_id : null;
			if (!documentId) {
				setMessage({ tone: "error", text: uploadErrorCopyFor({ kind: "unexpected" }) });
				return;
			}
			toast.success("Document uploaded");
			// Refresh coordination: invoke exactly once. Handle synchronous
			// throws AND rejected Promises. A list-refresh failure is
			// non-fatal because the upload already succeeded — it must never
			// reclassify the outcome as failure, never trigger another POST,
			// never expose raw error content, and never leak an unhandled
			// rejection.
			if (typeof onUploaded === "function") {
				try {
					const maybePromise = onUploaded();
					if (maybePromise && typeof maybePromise.then === "function") {
						maybePromise.catch(() => {
							// Upload succeeded; refresh failure is non-fatal.
						});
					}
				} catch (_syncThrow) {
					// Upload succeeded; a synchronous refresh failure is non-fatal.
				}
			}
			// Reset dialog state before closing.
			setFile(null);
			setTitle("");
			setTitleTouched(false);
			setMessage(null);
			if (typeof onClose === "function") onClose();
		} catch (err) {
			if (!mountedRef.current) return;
			const parsed = parseUploadError(err);
			if (parsed.kind === "silent") return; // 401 handled by shared interceptor
			setMessage({ tone: "error", text: uploadErrorCopyFor(parsed) });
		} finally {
			inFlightRef.current = false;
			if (mountedRef.current) setUploading(false);
		}
	};

	// Radix onOpenChange: while uploading, ignore all "close" attempts (Escape,
	// overlay click, close-icon).
	const handleOpenChange = (open) => {
		if (!open) {
			if (inFlightRef.current) return;
			if (typeof onClose === "function") onClose();
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent
				data-testid={KNOWLEDGE_BASE.uploadDialog}
				className="sm:max-w-lg"
				aria-busy={uploading ? "true" : "false"}
				onEscapeKeyDown={(event) => {
					if (uploading) event.preventDefault();
				}}
				onPointerDownOutside={(event) => {
					if (uploading) event.preventDefault();
				}}
				onInteractOutside={(event) => {
					if (uploading) event.preventDefault();
				}}
			>
				<DialogHeader>
					<DialogTitle data-testid={KNOWLEDGE_BASE.uploadDialogTitle}>
						Upload document
					</DialogTitle>
					<DialogDescription data-testid={KNOWLEDGE_BASE.uploadDialogDescription}>
						Add a PDF, DOCX, TXT, or Markdown file to this workspace's Knowledge Base.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-1">
						<Label htmlFor="kb-upload-title">Title</Label>
						<Input
							id="kb-upload-title"
							type="text"
							value={title}
							onChange={onTitleChange}
							maxLength={200}
							disabled={uploading}
							data-testid={KNOWLEDGE_BASE.uploadTitleInput}
							placeholder="A short human-readable title"
						/>
					</div>

					<div className="space-y-1">
						<Label htmlFor="kb-upload-file">File</Label>
						<input
							id="kb-upload-file"
							type="file"
							accept=".pdf,.docx,.txt,.md"
							onChange={onFileChange}
							disabled={uploading}
							data-testid={KNOWLEDGE_BASE.uploadFileInput}
							className="block w-full text-sm text-neutral-700 file:mr-3 file:rounded-md file:border-0 file:bg-[#F4F6F8] file:px-3 file:py-1.5 file:text-sm file:text-[#0B1F3A] hover:file:bg-neutral-200"
						/>
						{file && (
							<div className="mt-2 rounded-md border border-black/10 bg-white p-2 text-xs text-neutral-700">
								<div
									data-testid={KNOWLEDGE_BASE.uploadSelectedName}
									style={{ unicodeBidi: "plaintext" }}
									className="font-mono break-all"
								>
									{file.name}
								</div>
								<div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-neutral-600">
									<span data-testid={KNOWLEDGE_BASE.uploadSelectedType}>
										{typeLabel(file)}
									</span>
									<span data-testid={KNOWLEDGE_BASE.uploadSelectedSize}>
										{formatBytes(typeof file.size === "number" ? file.size : 0)}
									</span>
								</div>
							</div>
						)}
					</div>

					{message && (
						<div
							data-testid={KNOWLEDGE_BASE.uploadInlineMessage}
							role={message.tone === "error" ? "alert" : "status"}
							aria-live={message.tone === "error" ? "assertive" : "polite"}
							className={
								"rounded-md border p-2 text-xs " +
								(message.tone === "error"
									? "border-red-200 bg-red-50 text-red-800"
									: "border-blue-200 bg-blue-50 text-blue-800")
							}
						>
							{message.text}
						</div>
					)}
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="secondary"
						onClick={closeIfIdle}
						disabled={uploading}
						data-testid={KNOWLEDGE_BASE.uploadCancelButton}
					>
						Cancel
					</Button>
					<Button
						type="button"
						onClick={doUpload}
						disabled={uploading}
						data-testid={KNOWLEDGE_BASE.uploadSubmitButton}
					>
						{uploading ? "Uploading…" : "Upload"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
